import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import {
  FilterOptions,
  useServerFilterOptions,
} from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import {
  HeatActivitiesList,
  PaginatedHeatActivitiesListList,
} from '@shared/api/types';
import AddDataMenu from '@app/components/imports/scope-1-2-import/components/add-data-menu';
import { baseTableSchema } from '@shared/components/data-table/schema';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useCCF } from '../../ccf-context';
import HeatEditForm from './heating-edit-form';
import HeatingPreviewSheet from './heating-preview-sheet';
import { heatingTableColumns } from './heating-table-columns';

export const schema = baseTableSchema.extend({
  uuid: z.string().optional(),
});

interface HeatingTableProps
  extends DataTableProps<PaginatedHeatActivitiesListList> {}

export default function HeatingTable({
  data,
  isLoading,
  isPreviousData,
  isFetchedAfterMount,
}: HeatingTableProps) {
  const { scope3Supported } = useCCF();
  const [selectedActivityUuid, setSelectedActivityUuid] =
    useState<HeatActivitiesList['uuid']>();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const {
    showSimpleDialog: showHeatDialog,
    SimpleDialogComponent: HeatDialog,
    closeSimpleDialog: closeHeatDialog,
  } = useSimpleDialog();

  const { results: activities, count } = data || {};

  const pageCount = count ? Math.ceil(count / (perPage || 10)) : 0;

  const { timePeriodsFilter, sitesFilter } = useServerFilterOptions(
    ['timePeriods', 'sites'] as const,
    data?.filters || {}
  );

  const useFilterOptions: FilterOptions<['timePeriods', 'sites']> = useMemo(
    () => ({
      timePeriods: timePeriodsFilter,
      sites: sitesFilter,
    }),
    [timePeriodsFilter, sitesFilter]
  );

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: heatingTableColumns({
      filters: useFilterOptions,
    }),
    pageCount,
    getRowId: (row) => row.uuid,
    initialState: {
      columnVisibility: {
        uuid: false,
        period: false,
        scope3Emissions: !!scope3Supported,
      },
    },
  });
  return (
    <>
      <DataTable
        table={table}
        context={context}
        className="bg-gray-00"
        rowClassName={() => 'cursor-pointer hover:bg-gray-50'}
        onRowClick={(row) => {
          setSelectedActivityUuid(row?.original.uuid);
          showHeatDialog();
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
        tableLoading={!data && status === 'loading'}
        columnClassName={{
          scope3Emissions: 'border-l',
        }}
      >
        <DataTableToolbar table={table} filterOrder={['site', 'period']}>
          <HeatingPreviewSheet />
          <AddDataMenu
            categorySlug="heat"
            onAddManually={() => {
              setSelectedActivityUuid(undefined);
              showHeatDialog();
            }}
          />
        </DataTableToolbar>
      </DataTable>
      {HeatDialog({
        header: 'Add heating data',
        content: (
          <HeatEditForm
            activityUuid={selectedActivityUuid}
            closeDialog={closeHeatDialog}
          />
        ),
      })}
    </>
  );
}
